/**
 * Small helpers shared by the in-app help framework: query keys, DOM lookups for tour anchors,
 * route merging for tour steps and readable error messages.
 */

import type { HelpPageView, HelpTourView } from '@actschurch/ccms-sdk';
import { HELP_APPS } from './helpRegistry';

// ---------------------------------------------------------------- query keys

export const helpKeys = {
  all: ['help'] as const,
  pages: () => ['help', 'page'] as const,
  page: (pageKey: string) => ['help', 'page', pageKey] as const,
  /** The signed-in person's tour progress (per user, so a new sign-in never sees the last person's). */
  progress: (userId?: string | null) => ['help', 'progress', userId ?? ''] as const,
  /** The signed-in person's per-app help settings. */
  preferences: (userId?: string | null) => ['help', 'preferences', userId ?? ''] as const,
  askStatus: () => ['help', 'ask-status'] as const,
  adminPages: (appKey?: string | null) => ['help', 'admin', 'pages', appKey ?? 'all'] as const,
};

/** Five minutes: help content changes rarely; admin saves update the cache directly. */
export const HELP_STALE_MS = 5 * 60_000;

// ---------------------------------------------------------------- apps

/** The app part of a page key ("events.sources" gives "events"), as the System API's HelpKeys.AppOf. */
export function appKeyOfPageKey(pageKey: string): string {
  const dot = pageKey.indexOf('.');
  return dot > 0 ? pageKey.slice(0, dot) : pageKey;
}

/** The app's display name from the help registry ("rides" gives "Carpool"); otherwise a label from its key. */
export function helpAppTitle(appKey: string): string {
  const def = HELP_APPS.find((a) => a.appKey === appKey);
  if (def?.title) return def.title;
  if (appKey.length <= 3) return appKey.toUpperCase();
  const words = appKey.replace(/[-_.]+/g, ' ').trim();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

// ---------------------------------------------------------------- tours

/** The tour a page offers: the first active tour with steps (by title for a stable order). */
export function primaryTour(page: HelpPageView | null | undefined): HelpTourView | null {
  const tours = (page?.tours ?? []).filter((t) => t.isActive && (t.steps?.length ?? 0) > 0);
  if (tours.length === 0) return null;
  return tours.find((t) => t.autoStart) ?? tours[0];
}

export function activeTours(page: HelpPageView | null | undefined): HelpTourView[] {
  return (page?.tours ?? []).filter((t) => t.isActive && (t.steps?.length ?? 0) > 0);
}

export function sortedSteps(tour: HelpTourView) {
  return [...(tour.steps ?? [])].sort((a, b) => a.sortOrder - b.sortOrder);
}

// ---------------------------------------------------------------- anchors

function cssEscape(value: string): string {
  if (typeof CSS !== 'undefined' && typeof CSS.escape === 'function') return CSS.escape(value);
  return value.replace(/["\\]/g, '\\$&');
}

/** True when the element takes up space on screen (not display:none, not in a hidden tab panel). */
export function isVisible(el: Element | null): el is HTMLElement {
  if (!el || !(el as HTMLElement).isConnected) return false;
  const rects = el.getClientRects();
  if (rects.length === 0) return false;
  const r = el.getBoundingClientRect();
  return r.width > 0 || r.height > 0;
}

/** First visible element with this data-help-id. */
export function findAnchor(helpId: string): HTMLElement | null {
  if (!helpId || typeof document === 'undefined') return null;
  const all = document.querySelectorAll(`[data-help-id="${cssEscape(helpId)}"]`);
  for (const el of Array.from(all)) {
    if (isVisible(el)) return el as HTMLElement;
  }
  return null;
}

/** True when any visible anchor of the page is on screen. */
export function pageHasAnchors(pageKey: string): boolean {
  if (typeof document === 'undefined') return false;
  const all = document.querySelectorAll(`[data-help-id^="${cssEscape(pageKey)}."]`);
  return Array.from(all).some((el) => isVisible(el));
}

const sleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

/** Polls until the check passes or the time runs out. Resolves with the last result. */
export async function waitFor<T>(check: () => T | null | false, timeoutMs: number, isCancelled: () => boolean, intervalMs = 120): Promise<T | null> {
  const end = Date.now() + Math.max(0, timeoutMs);
  for (;;) {
    if (isCancelled()) return null;
    const found = check();
    if (found) return found;
    if (Date.now() >= end) return null;
    await sleep(intervalMs);
  }
}

export { sleep };

// ---------------------------------------------------------------- routes

/**
 * Resolves a tour step route against the current location.
 * "?tab=plan" merges into the current search params (an empty value removes the key);
 * anything else is returned as is for react-router.
 */
export function resolveStepRoute(route: string, pathname: string, search: string): { to: string; changed: boolean } {
  const trimmed = route.trim();
  if (!trimmed) return { to: pathname + search, changed: false };
  if (trimmed.startsWith('?')) {
    const next = new URLSearchParams(search);
    new URLSearchParams(trimmed.slice(1)).forEach((value, key) => {
      if (value === '') next.delete(key);
      else next.set(key, value);
    });
    const qs = next.toString();
    const to = pathname + (qs ? `?${qs}` : '');
    return { to, changed: to !== pathname + search };
  }
  return { to: trimmed, changed: trimmed !== pathname + search };
}

/** Internal console link (in-app navigation) vs anything else. */
export function isInternalHref(href: string | null | undefined): href is string {
  if (!href) return false;
  return href.startsWith('/') && !href.startsWith('//');
}

// ---------------------------------------------------------------- errors

const TECHNICAL = /JSON|ResponseData|Request failed with status|status \d{3}|Failed to fetch|NetworkError|Load failed|undefined|null/i;

function readable(message: unknown): string | null {
  if (typeof message !== 'string') return null;
  const m = message.trim();
  if (!m || TECHNICAL.test(m)) return null;
  return m;
}

export function helpErrorMessage(err: unknown, fallback: string): string {
  return readable((err as { message?: unknown } | null)?.message) ?? fallback;
}

/** Friendly text for a failed question; 429 = the hourly limit. */
export function friendlyAskError(err: unknown): string {
  const e = err as { status?: number; message?: unknown } | null;
  if (e?.status === 429) {
    return readable(e.message) ?? 'You have asked a lot of questions in the last hour. Try again a little later.';
  }
  if (e?.status === 401) return 'Your session has ended. Sign in again to ask.';
  return readable(e?.message) ?? 'AI help is not available right now. Try again later.';
}
